// Companion/blocks/draftQuestionText.tsx
//
// How a chat-authored draft question's text reaches the screen (ORA-934/939).
//
// Every other block renders content Ora's own rows hold, which is why the
// question block may hand its stem to `renderVignetteContent` — a tag
// allow-list over `html-react-parser` that passes `img`, `a` and `svg`. A
// draft's stem, options and explanations were written by a model. None of that
// text may enter that pipeline, or `dangerouslySetInnerHTML`, or any other
// trusted-HTML path; it renders as text.
//
// Two tags still have to work, because a well-formed item uses them: `HCO<sub>3
// </sub><sup>-</sup>`, `Na<sup>+</sup>`. So this is deliberately not a
// sanitizer and not a parser — it is a two-tag tokenizer. It recognises exactly
// `<sup>…</sup>` and `<sub>…</sub>`, with no attributes, and turns those into
// React elements. Everything else — `<script>`, `<img>`, an attribute on a
// `<sup>`, an unclosed tag, a stray `<` — is left in the string and rendered as
// a text child, which React escapes. There is no branch in which a tag this
// function does not recognise becomes markup.

import type { ReactNode } from 'react';

/**
 * `<sup>` or `<sub>` with no attributes, and its closing tag. The
 * back-reference is what makes `<sup>x</sub>` not a match: a mismatched pair is
 * text, like any other markup this does not know.
 */
const SUP_SUB = /<(sup|sub)>([\s\S]*?)<\/\1>/gi;

/**
 * One run of draft text as React children: the string, with `<sup>`/`<sub>`
 * runs replaced by elements. Nesting is not supported — the content of a
 * `<sup>` is text, which is all an item needs it for.
 */
export const renderDraftText = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  // `exec` with /g walks the string; the regex is local to this call.
  const pattern = new RegExp(SUP_SUB.source, 'gi');

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));

    const Tag = match[1].toLowerCase() === 'sup' ? 'sup' : 'sub';
    nodes.push(<Tag key={`${Tag}-${match.index}`}>{match[2]}</Tag>);
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
};

/**
 * A model-written block of prose as paragraphs. Blank lines are the only
 * paragraph mechanism in the item format (the vetted explanations use the same
 * convention, where `white-space: pre-line` preserves them), so this is where a
 * draft's `\n\n` becomes real paragraphs instead of one run-on wall.
 */
export const draftTextParagraphs = (text: string): string[] =>
  text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '');
